/**
 * GitLab Pipeline Topology 工具函数
 * 将 GitLab Jobs 转换为拓扑图数据模型
 */

import { EdgeModel, GraphModel, NodeModel } from '@patternfly/react-topology';

export interface GitLabJob {
  id: number;
  name: string;
  status: string;
  stage: string;
  duration?: number;
  web_url: string;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  artifacts_file?: {
    filename: string;
    size: number;
  };
  runner?: {
    description: string;
  };
}

export interface GitLabPipeline {
  id: number;
  iid: number;
  project_id: number;
  status: string;
  source: string;
  ref: string;
  sha: string;
  web_url: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  finished_at?: string;
  duration?: number;
  user?: {
    id: number;
    username: string;
    name: string;
    avatar_url?: string;
  };
  // Commit 信息（部分 API 返回）
  commit?: {
    id: string;
    title: string;
    message: string;
    author_name?: string;
    author_email?: string;
    authored_date?: string;
    committer_name?: string;
    committer_email?: string;
    committed_date?: string;
    created_at?: string;
    // 如果 commit 关联了 GitLab 用户，会有 author 对象
    author?: {
      id: number;
      name: string;
      username: string;
      avatar_url?: string;
    };
  };
}

export interface PipelineMixedNodeModel extends NodeModel {
  data?: {
    job?: GitLabJob;
    status?: string;
    stage?: string;
  };
}

/**
 * 将 GitLab Jobs 转换为图数据模型
 */
export const getGraphDataModel = (
  pipeline: GitLabPipeline,
  jobs: GitLabJob[],
): {
  graph: GraphModel;
  nodes: PipelineMixedNodeModel[];
  edges: EdgeModel[];
} => {
  // 按 stage 分组
  const jobsByStage = jobs.reduce((acc, job) => {
    const stage = job.stage || 'unknown';
    if (!acc[stage]) {
      acc[stage] = [];
    }
    acc[stage].push(job);
    return acc;
  }, {} as Record<string, GitLabJob[]>);

  const stages = Object.keys(jobsByStage).sort();

  const nodes: PipelineMixedNodeModel[] = [];
  const edges: EdgeModel[] = [];

  let xOffset = 0;
  const stageSpacing = 250;
  const jobSpacing = 80;

  // 为每个 Stage 创建节点
  stages.forEach((stage, stageIndex) => {
    const stageJobs = jobsByStage[stage];
    
    stageJobs.forEach((job, jobIndex) => {
      const nodeId = `job-${job.id}`;
      
      // 创建节点
      nodes.push({
        id: nodeId,
        type: 'task',
        label: job.name,
        width: 180,
        height: 33,
        data: {
          job,
          status: job.status,
          stage: stage,
        },
      });

      // 如果不是第一个 stage，创建从前一个 stage 到当前 stage 的边
      if (stageIndex > 0) {
        const prevStage = stages[stageIndex - 1];
        const prevStageJobs = jobsByStage[prevStage];
        
        // 连接到前一个 stage 的所有 jobs（简化处理）
        prevStageJobs.forEach(prevJob => {
          edges.push({
            id: `edge-${prevJob.id}-${job.id}`,
            type: 'edge',
            source: `job-${prevJob.id}`,
            target: nodeId,
          });
        });
      }
    });

    xOffset += stageSpacing;
  });

  const graph: GraphModel = {
    id: `pipeline-${pipeline.id}-graph`,
    type: 'graph',
    layout: 'Dagre',
  };

  return { graph, nodes, edges };
};

